import React, { useCallback, useMemo, useState, useContext } from 'react'
import { MainButton } from '../../Button/MainButton'
import useBoardingVisit from '../../../hooks/useBoardingVisit'
import BoardingWizardContext from './BoardingWizardContext'
import OptionsMenu from '../../Common/OptionsMenu/OptionsMenu'
import SectionFormContainer from '../components/SectionFormContainer'
import InfoDialog from '../components/InfoDialog'
import SubOptionsDialog from '../components/SubOptionsDialog'
import {
	FONT_WEIGHTS,
	TIME_BLOCKS,
	STANDARD_DATE_FORMAT,
	capitilizeMultipleWords,
	formatNumsToDollars
} from '../../../utils'
import { MenuItem, Checkbox, makeStyles } from '@material-ui/core'
import DatePicker, { DateObject } from 'react-multi-date-picker'
import { gray } from '../../../utils/colors'
import StyledMultiSelect from '../../Common/StyledMultiSelect'
import StyledAlert from '../../Common/StyledAlert'
import { purple } from '@material-ui/core/colors'
import moment from 'moment'
import useSelectedHousehold from '../../../hooks/useSelectedHousehold'
import { showError } from '../../../actions/misc/misc.actions'
import { useDispatch } from 'react-redux'

const DEFAULT_POPUP_CONTENT = { title: '', description: '' }

const useStyles = makeStyles(() => ({
	checkBoxContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '.5rem',
		marginTop: '1rem',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		'& .MuiCheckbox-root': {
			padding: 0
		}
	},
	subOptionsSection: { display: 'flex', flexDirection: 'column', gap: '.6rem', marginBottom: '1rem' },
	sectionHeader: {
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '0.75rem',
		textTransform: 'uppercase',
		letterSpacing: '0.047rem'
	},
	mainActions: {
		display: 'flex',
		flexDirection: 'row',
		alignSelf: 'flex-start',
		gap: '1rem',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		}
	},
	selectContainer: {
		flex: 1,
		display: 'flex',
		'& *.MuiFilledInput-underline': { minWidth: 'unset' },
		'& .MuiFormControl-root': {
			flex: 1
		}
	},
	addonDatesPickerContainer: {
		'& .rmdp-container': {
			width: '100%',
			'& .rmdp-calendar-container-mobile': {
				position: 'absolute !important',
				backgroundColor: 'rgba(255,255,255,.97)',
				left: 0,
				top: 0,
				width: '100%',
				height: '100%',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center'
			},
			'& .rmdp-week-day': {
				color: purple[500]
			},
			'& .rmdp-day.rmdp-selected span:not(.highlight)': {
				backgroundColor: purple[500],
				boxShadow: 'none'
			},
			'& .rmdp-button.rmdp-action-button': {
				color: purple[500]
			},
			'& .rmdp-arrow-container:hover': {
				backgroundColor: purple[500],
				boxShadow: 'none'
			},
			'& .rmdp-arrow-container.disabled:hover': {
				backgroundColor: 'transparent'
			},
			'& .rmdp-day:not(.rmdp-disabled):not(.rmdp-day-hidden) span:hover': {
				backgroundColor: purple[200]
			}
		}
	}
}))

const AddOnSelection = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const { isVipOrPetCareOnDate } = useSelectedHousehold()

	const {
		wizardContextProvider: { wizard_state, setWizardAttributeAndValues }
	} = useContext(BoardingWizardContext)

	const {
		attr,
		subtext = '',
		name,
		_id: pet_id,
		petNames,
		copy_to_all_attr,
		first_view_of_multiple = false
	} = props
	const { loading, facility, updating } = useBoardingVisit()

	const [infoPopupOpen, setInfoPopupOpen] = useState(false)
	const [subOptionsDialogOpen, setSubOptionsDialogOpen] = useState(false)
	const [currentPopupContent, setCurrentPopupContent] = useState(DEFAULT_POPUP_CONTENT)
	const [currentAddOnDates, setCurrentAddOnDates] = useState([])
	const [currentAddOnTimeblocks, setCurrentAddonTimeblocks] = useState([])
	const [currentChosenAddOn, setCurrentChosenAddOn] = useState(null)

	const title = useMemo(
		() => `Enrichments for ${wizard_state[copy_to_all_attr] === true ? petNames : name}`,
		[name, wizard_state, copy_to_all_attr]
	)

	const firstDay = useMemo(() => {
		const { dates } = wizard_state
		const [day] = dates
		return day || null
	}, [wizard_state])

	const lastDay = useMemo(() => {
		const { dates } = wizard_state
		const lastDay = dates[dates.length - 1]
		return lastDay || null
	}, [wizard_state])

	const calendarDateToDisplay = useMemo(() => {
		if (firstDay) {
			const firstDateToMoment = moment(firstDay)
			return new DateObject({
				year: firstDateToMoment.get('year'),
				month: firstDateToMoment.get('month') + 1,
				day: 1
			})
		}
	}, [firstDay])

	const hasDaycareFirstDay = useMemo(() => {
		const { first_day_daycare } = wizard_state
		return first_day_daycare
	}, [wizard_state])

	const hasDaycareLastDay = useMemo(() => {
		const { last_day_daycare } = wizard_state
		return last_day_daycare
	}, [wizard_state])

	const hasEarlyDropoff = useMemo(() => {
		const { early_dropoff } = wizard_state
		return early_dropoff
	}, [wizard_state])

	const hasExtendedPickup = useMemo(() => {
		const { extended_pickup } = wizard_state
		return extended_pickup
	}, [wizard_state])

	const showCopyToAllCheckbox = useMemo(() => {
		return (
			first_view_of_multiple && wizard_state['pets'].length > 1 && copy_to_all_attr && copy_to_all_attr > ''
		)
	}, [first_view_of_multiple, wizard_state, copy_to_all_attr])

	const handleCopyToAll = useCallback(
		(e) => {
			if (e.target && e.target.checked !== undefined) {
				const newValues = { ...wizard_state }
				newValues[copy_to_all_attr] = e.target.checked
				setWizardAttributeAndValues(newValues)
			}
		},
		[wizard_state, setWizardAttributeAndValues, attr, copy_to_all_attr]
	)

	const handleInfoButtonClicked = useCallback(
		(content) => {
			setInfoPopupOpen(true)
			setCurrentPopupContent(content)
		},
		[setInfoPopupOpen]
	)

	const handleInfoPopupClose = useCallback(() => {
		setInfoPopupOpen(false)
		setTimeout(() => {
			setCurrentPopupContent(DEFAULT_POPUP_CONTENT)
		}, 400)
	}, [setInfoPopupOpen, setCurrentPopupContent])

	const handleDeselection = useCallback(
		(addon) => {
			const newValues = { ...wizard_state }
			let newAddOnArray = []
			const { name } = addon
			const hasAddon = wizard_state[attr].findIndex((a) => a.name === name && a.pet_id === pet_id)
			if (hasAddon > -1) {
				newAddOnArray = newValues[attr].filter((a) => {
					if (a.category === 'meal') {
						return true
					}
					if (a.pet_id !== pet_id) {
						return true
					}
					return a.name !== name && a.pet_id === pet_id
				})
				newValues[attr] = newAddOnArray
				setWizardAttributeAndValues(newValues)
			}
		},
		[wizard_state, setWizardAttributeAndValues, attr, pet_id]
	)

	const handleFinalSelection = useCallback(
		(addons = []) => {
			if (addons.length < 1) {
				return
			}

			const newValues = { ...wizard_state }
			let newAddOnArray = []

			const [baseAddon] = addons

			const hasAddon = wizard_state[attr].findIndex(
				(a) => a.name === baseAddon.name && a.pet_id === baseAddon.pet_id
			)

			if (hasAddon > -1) {
				newAddOnArray = newValues[attr].filter((a) => {
					if (a.category === 'meal') {
						return true
					}
					if (a.pet_id !== pet_id) {
						return true
					}
					return a.name !== baseAddon.name && a.pet_id === baseAddon.pet_id
				})

				newAddOnArray = [...newAddOnArray, ...addons]
			} else {
				newAddOnArray = [...newValues[attr], ...addons]
			}

			newValues[attr] = newAddOnArray
			setWizardAttributeAndValues(newValues)
		},
		[wizard_state, setWizardAttributeAndValues, attr, pet_id]
	)

	const handleSelection = useCallback(
		(addon) => {
			const isVipOnVisitDate = isVipOrPetCareOnDate(firstDay, facility?._id).vip

			if (addon.vip_only && !isVipOnVisitDate) {
				dispatch(showError('This enrichment is available for VIP members only.'))
				return
			}

			const addonToAdd = addon.vip_only && isVipOnVisitDate ? { ...addon, price: 0 } : addon
			const newValues = { ...wizard_state }
			let newAddOnArray = []
			const { name, addon_choice_id, boarding_default_time = TIME_BLOCKS.Midday } = addonToAdd
			const hasAddon = wizard_state[attr].findIndex((a) => a.name === name && a.pet_id === pet_id)
			if (hasAddon > -1) {
				if (!addon_choice_id) {
					newAddOnArray = newValues[attr].filter((a) => {
						if (a.category === 'meal') {
							return true
						}
						if (a.pet_id !== pet_id) {
							return true
						}
						return a.name !== name && a.pet_id === pet_id
					})
				} else {
					newAddOnArray = [...newValues[attr]]
					newAddOnArray[hasAddon] = { ...addonToAdd, time: boarding_default_time, pet_id }
				}
			} else {
				newAddOnArray = [...newValues[attr], { ...addonToAdd, time: boarding_default_time, pet_id }]
			}

			newValues[attr] = newAddOnArray
			setWizardAttributeAndValues(newValues)

			if (addon_choice_id) {
				setCurrentChosenAddOn((prev) => ({ ...prev, addon_choice_id }))
			}
		},
		[
			wizard_state,
			setWizardAttributeAndValues,
			attr,
			pet_id,
			setCurrentChosenAddOn,
			firstDay,
			isVipOrPetCareOnDate,
			dispatch
		]
	)

	const addOnCategories = useMemo(() => {
		if (!facility || !facility.addons_categories) {
			return []
		}
		return facility.addons_categories.filter(
			({ grooming_service, should_launch_grooming_booking }) =>
				!grooming_service && !should_launch_grooming_booking
		)
	}, [facility])

	const facilityAddons = useMemo(() => {
		if (!facility || !facility.addOnTypes) {
			return []
		}
		return facility.addOnTypes
	}, [facility])

	const addOnServices = useMemo(() => {
		if (!facility || !facility.add_on_services) {
			return []
		}

		const services = []

		addOnCategories.forEach(({ _id: categoryId }) => {
			const categoryServices = facility.add_on_services[categoryId] || []

			for (const service of categoryServices) {
				const { active, access, category, boarding_time_blocks, available_boarding } = service

				if (!available_boarding || !active || access === 'Admin Only') {
					continue
				}

				if (category === 'schedule' && !boarding_time_blocks && !boarding_time_blocks.length) {
					continue
				}

				services.push(service)
			}
		})
		return services
	}, [addOnCategories, facility, wizard_state])

	const options = useMemo(() => {
		if (!facility) {
			return []
		}

		const opts = []

		addOnServices.forEach((addon, i) => {
			const {
				name,
				description,
				addon_choices = [],
				price,
				min_price,
				max_price,
				training_service = false,
				vip_only = false
			} = addon
			const hasSuboptions = addon_choices.length > 0
			const selected = wizard_state[attr].find((addon) => addon.name === name && addon.pet_id === pet_id)
			const title = capitilizeMultipleWords(name, ' ')
			const isVipOnVisitDate = isVipOrPetCareOnDate(firstDay, facility?._id).vip
			const vipUnavailable = vip_only && !isVipOnVisitDate

			let descriptionFormatted = vip_only && isVipOnVisitDate ? 'Included' : formatNumsToDollars(price)

			if (max_price > min_price) {
				descriptionFormatted = `${formatNumsToDollars(min_price)} - ${formatNumsToDollars(max_price)}`
			}

			// if suboptions, show base and highest price, if there is a higher price than the base.
			if (hasSuboptions) {
				let largestAmount = price
				addon_choices.forEach(({ upcharge }) => {
					const total = price + upcharge
					if (total > largestAmount) {
						largestAmount = total
					}
				})

				if (largestAmount > price) {
					descriptionFormatted = `${formatNumsToDollars(price)} - ${formatNumsToDollars(largestAmount)} `
				}
			}

			// if option is selected and has a suboption selected, then show the suboption name and price
			if (selected && selected.addon_choice_id && selected.addon_choice_id > '') {
				const suboption = addon_choices.find(({ id }) => id === selected.addon_choice_id)
				if (suboption) {
					descriptionFormatted = `${suboption.name} - ${formatNumsToDollars(price + suboption.upcharge)}`
				}
			}

			const option = {
				title,
				description: descriptionFormatted,
				id: i,
				disabled: loading || vipUnavailable,
				selected: selected,
				training_service,
				onSelected: () => {
					if (vipUnavailable) {
						return
					}
					if (selected) {
						handleDeselection(addon)
					} else {
						if (!hasSuboptions) {
							handleSelection(addon)
						}
						setCurrentChosenAddOn(addon)
						setSubOptionsDialogOpen(true)
					}
				},
				onInfoButtonClicked: () => handleInfoButtonClicked({ title, description })
			}
			opts.push(option)
		})

		return opts
	}, [
		facility,
		wizard_state,
		attr,
		handleInfoButtonClicked,
		loading,
		addOnServices,
		pet_id,
		setCurrentChosenAddOn,
		handleDeselection,
		handleSelection,
		firstDay,
		isVipOrPetCareOnDate
	])

	const timeBlockMenuItems = useMemo(() => {
		if (!currentChosenAddOn) {
			return []
		}
		const menuItems = []
		let timeBlocks = []

		let { boarding_time_blocks } = currentChosenAddOn

		if (!boarding_time_blocks) {
			const baseAddon = facilityAddons.find(({ _id }) => _id === currentChosenAddOn._id)
			if (baseAddon) {
				boarding_time_blocks = baseAddon.boarding_time_blocks
			}
		} else {
			timeBlocks = boarding_time_blocks.length > 0 ? boarding_time_blocks : [TIME_BLOCKS.Midday]
		}
		timeBlocks.forEach((timeBlock, i) => {
			menuItems.push(
				<MenuItem key={i} value={timeBlock}>
					<Checkbox checked={currentAddOnTimeblocks.indexOf(timeBlock) > -1} />
					{capitilizeMultipleWords(timeBlock)}
				</MenuItem>
			)
		})

		return menuItems
	}, [currentChosenAddOn, currentAddOnTimeblocks, facilityAddons, wizard_state, attr, pet_id])

	const submenuOptions = useMemo(() => {
		const suboptions = []
		if (currentChosenAddOn) {
			const { name, addon_choices = [], addon_choice_id, price } = currentChosenAddOn
			const hasSuboptions = addon_choices.length > 0
			const selected = wizard_state[attr].find((addon) => addon.name === name && addon.pet_id === pet_id)
			const title = capitilizeMultipleWords(name, ' ')

			if (!hasSuboptions) {
				suboptions.push({
					title,
					description: formatNumsToDollars(price),
					selected,
					onSelected: () => null
				})
			} else {
				addon_choices.forEach((addon, i) => {
					const { name, upcharge, id } = addon
					const cost = price + upcharge
					const option = {
						title: capitilizeMultipleWords(name, ' '),
						description: formatNumsToDollars(cost),
						id: i,
						selected: addon_choice_id === id,
						onSelected: () => {
							handleSelection({ ...currentChosenAddOn, price: cost, addon_choice_id: id })
						}
					}
					suboptions.push(option)
				})
			}
		}
		return suboptions
	}, [currentChosenAddOn, wizard_state, attr, pet_id])

	const handleOnSaveAddon = useCallback(() => {
		const newAddOnArray = []
		const { early_dropoff, extended_pickup, first_day_daycare, last_day_daycare } = wizard_state

		for (const date of currentAddOnDates) {
			for (const timeblock of currentAddOnTimeblocks) {
				if (date === firstDay) {
					if (timeblock === TIME_BLOCKS.Morning && !early_dropoff) {
						continue
					}
					if (timeblock === TIME_BLOCKS.Midday && !first_day_daycare) {
						continue
					}
				}
				if (date === lastDay) {
					if (timeblock === TIME_BLOCKS.Evening && !extended_pickup) {
						continue
					}
					if (timeblock === TIME_BLOCKS.Midday && !last_day_daycare) {
						continue
					}
				}
				const newAddon = { ...currentChosenAddOn, time: timeblock, pet_id, date }
				newAddOnArray.push(newAddon)
			}
		}
		handleFinalSelection(newAddOnArray)
		setSubOptionsDialogOpen(false)
		setCurrentChosenAddOn(null)
		setCurrentAddOnDates([])
		setCurrentAddonTimeblocks([])
	}, [
		firstDay,
		lastDay,
		wizard_state,
		setSubOptionsDialogOpen,
		currentChosenAddOn,
		currentAddOnDates,
		currentAddOnTimeblocks,
		handleFinalSelection
	])

	const handleOnCancelAddOn = useCallback(() => {
		if (currentChosenAddOn) {
			handleDeselection(currentChosenAddOn)
		}
		setSubOptionsDialogOpen(false)
		setCurrentChosenAddOn(null)
		setCurrentAddOnDates([])
		setCurrentAddonTimeblocks([])
	}, [
		currentChosenAddOn,
		handleDeselection,
		setSubOptionsDialogOpen,
		setCurrentAddOnDates,
		setCurrentAddonTimeblocks
	])

	const handleAddOnDatesChange = useCallback(
		(dateArray) => {
			const newDates = dateArray.map((date) => date.format(STANDARD_DATE_FORMAT))
			setCurrentAddOnDates(newDates)
		},
		[setCurrentAddOnDates]
	)

	const handleAddOnTimeBlocksChange = useCallback(
		(timeBlockArr) => {
			setCurrentAddonTimeblocks(() => {
				return [...timeBlockArr]
			})
		},
		[setCurrentAddonTimeblocks]
	)

	const saveAllowed = useMemo(() => {
		const { addon_choices, addon_choice_id } = currentChosenAddOn || {}
		if (addon_choices && addon_choices.length > 0 && !addon_choice_id) {
			return false
		}
		return currentAddOnDates.length > 0 && currentAddOnTimeblocks.length > 0
	}, [currentAddOnDates, currentAddOnTimeblocks, currentChosenAddOn])

	const renderCalendarDay = useCallback(
		(day) => {
			if (
				!day ||
				!currentChosenAddOn ||
				!currentChosenAddOn.blocked_dates ||
				!currentChosenAddOn.days_of_week
			) {
				return null
			}

			const blockedDaysOfWeek = []
			const { days_of_week = {} } = currentChosenAddOn
			for (const day in days_of_week) {
				if (days_of_week[day] === 0) {
					blockedDaysOfWeek.push(day.toLowerCase())
				}
			}
			const dayDayofWeek = day.date.format('dddd').toLowerCase()
			if (blockedDaysOfWeek.includes(dayDayofWeek)) {
				return { disabled: true, style: { textDecoration: 'line-through' } }
			}

			if (currentChosenAddOn.blocked_dates && currentChosenAddOn.blocked_dates.length < 1) {
				return null
			}
			const dateFormatted = day.date.format(STANDARD_DATE_FORMAT)

			if (currentChosenAddOn.blocked_dates.includes(dateFormatted)) {
				return { disabled: true, style: { textDecoration: 'line-through' } }
			}
		},
		[currentChosenAddOn]
	)

	const hasAlertMessage = useMemo(() => {
		let alertMessage = ''

		const datesIncludesFirstDay = currentAddOnDates.includes(firstDay)
		const datesIncludesLastDay = currentAddOnDates.includes(lastDay)
		const hasMorning = currentAddOnTimeblocks.includes(TIME_BLOCKS.Morning)
		const hasMidday = currentAddOnTimeblocks.includes(TIME_BLOCKS.Midday)
		const hasEvening = currentAddOnTimeblocks.includes(TIME_BLOCKS.Evening)

		if (datesIncludesFirstDay) {
			if (hasDaycareFirstDay && !hasEarlyDropoff && hasMorning) {
				alertMessage = `Morning enrichment not available on first day without early dropoff.`
			}
			if (!hasDaycareFirstDay) {
				if (hasMorning && hasMidday) {
					alertMessage =
						'Morning and midday enrichments not available on first day without daycare and early dropoff.'
				} else if (hasMorning && !hasMidday) {
					alertMessage =
						'Morning enrichment not available on first day without daycare and early dropoff.'
				} else if (hasMidday && !hasMorning) {
					alertMessage = 'Midday enrichment not available on first day without daycare.'
				}
			}
		}

		if (datesIncludesLastDay) {
			if (hasDaycareLastDay && !hasExtendedPickup && hasEvening) {
				alertMessage = `${alertMessage}${
					alertMessage > '' ? ' ' : ''
				}Evening enrichment not available on last day without extended pickup.`
			}
			if (!hasDaycareLastDay) {
				if (hasEvening && hasMidday) {
					alertMessage = `${alertMessage}${
						alertMessage > '' ? ' ' : ''
					}Midday and evening enrichments not available on last day without daycare and extended pickup.`
				} else if (hasMidday && !hasEvening) {
					alertMessage = `${alertMessage}${
						alertMessage > '' ? ' ' : ''
					}Midday enrichment not available on last day without daycare.`
				} else if (hasEvening && !hasMidday) {
					alertMessage = `${alertMessage}${
						alertMessage > '' ? ' ' : ''
					}Evening enrichment not available on last day without daycare and extended pickup.`
				}
			}
		}

		if (alertMessage > '') {
			alertMessage += ` ${currentChosenAddOn.name} Enrichments will be scheduled for all other valid timeblocks and dates.`
		}

		return alertMessage
	}, [
		firstDay,
		lastDay,
		hasDaycareFirstDay,
		hasDaycareLastDay,
		hasEarlyDropoff,
		hasExtendedPickup,
		currentChosenAddOn,
		currentAddOnDates,
		currentAddOnTimeblocks
	])

	return (
		<>
			<InfoDialog
				onClose={handleInfoPopupClose}
				open={infoPopupOpen}
				title={currentPopupContent.title}
				description={currentPopupContent.description}
			/>
			<SubOptionsDialog
				title={currentChosenAddOn?.name || ''}
				description={currentChosenAddOn?.description || ''}
				onClose={handleOnCancelAddOn}
				open={subOptionsDialogOpen}
				numberOfOptions={submenuOptions.length}>
				<div className={classes.subOptionsSection}>
					<div className={classes.sectionHeader}>Enrichment Type</div>
					<OptionsMenu options={submenuOptions} />
				</div>
				<div className={classes.subOptionsSection}>
					<div className={classes.sectionHeader}>Date(s)</div>
					<div className={classes.addonDatesPickerContainer}>
						<DatePicker
							className="rmdp-mobile"
							multiple={true}
							offsetX={0}
							offsetY={0}
							zIndex={1000}
							calendarPosition="bottom-center"
							disabled={false}
							minDate={firstDay}
							maxDate={lastDay}
							currentDate={calendarDateToDisplay}
							value={currentAddOnDates}
							onChange={handleAddOnDatesChange}
							placeholder="Selected Dates"
							format="YYYY-MM-DD"
							mapDays={renderCalendarDay}
							style={{
								width: '100%',
								height: 56,
								margin: 0,
								borderBottomLeftRadius: 0,
								borderBottomRightRadius: 0,
								backgroundColor: gray[100],
								border: `1px solid ${gray[500]}`,
								paddingLeft: 16,
								fontSize: '1rem',
								fontFamily: 'Nunito',
								color: gray[800]
							}}
						/>
					</div>
				</div>
				<div className={classes.subOptionsSection}>
					<div className={classes.sectionHeader}>Timeblock(s)</div>
					<StyledMultiSelect
						label="Timeblocks"
						values={currentAddOnTimeblocks}
						onValuesChanged={handleAddOnTimeBlocksChange}
						menuItems={timeBlockMenuItems}
					/>
				</div>
				{hasAlertMessage > '' ? (
					<StyledAlert text={hasAlertMessage} style={{ marginBottom: '.8rem' }} type="error" />
				) : null}
				<div className={classes.mainActions}>
					<MainButton
						style={{ paddingTop: '6px' }}
						variant="contained"
						label="Save"
						onClick={handleOnSaveAddon}
						disabled={updating || !saveAllowed}
					/>
					<MainButton label="Cancel" onClick={handleOnCancelAddOn} disabled={updating} />
				</div>
			</SubOptionsDialog>
			<SectionFormContainer title={title} subtext={subtext}>
				{options.length > 0 ? <OptionsMenu options={options} /> : 'No enrichments available.'}
				{showCopyToAllCheckbox && options.length > 0 ? (
					<div className={classes.checkBoxContainer}>
						<Checkbox onChange={handleCopyToAll} checked={wizard_state[copy_to_all_attr] === true} />
						<div>Copy schedule for all dogs</div>
					</div>
				) : null}
			</SectionFormContainer>
		</>
	)
}

export default AddOnSelection
